import { Paper, Theme, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectFeatureFlagAgentCart } from '../../redux/features/config/config.selector';
import useEventListener from '../../hooks/useEventListener.hooks';
import useAISuggestionActions from './useAISuggestionActions.hooks';
import Colors from '../../utils/color';

const useStyles = makeStyles((theme: Theme) => ({
  // Section: heading + cards, mirroring LiveTranscriptCard / ReminderCard so the
  // left pane reads as a consistent stack of titled sections.
  section: {
    flexShrink: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  sectionIcon: {
    color: Colors.readBack,
    fontSize: '1rem',
    flexShrink: 0,
  },
  sectionTitle: {
    color: Colors.readBack,
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    border: '1px solid #c5cae9',
    backgroundColor: '#eef1fb',
    borderRadius: '8px',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  title: {
    fontWeight: 'bold !important' as 'bold',
    // Match the transcript message text size (0.75rem / 12px).
    fontSize: '0.75rem',
  },
  reason: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
}));

/**
 * Renders incoming AI suggestions (the "advise" side of the co-pilot) at the
 * bottom of the left pane. The human agent stays the driver: a suggestion is
 * never applied automatically — only on an explicit Accept.
 *
 * Accept / Dismiss are available both as card buttons and via Alt+A / Alt+D
 * (same shared path, see useAISuggestionActions).
 *
 * TODO (driver-rule gate): render only post-interception (isAIAutoMode === false).
 * Left ungated for now so the wire is easy to verify; wire the gate with the
 * apply step.
 */
const AISuggestions = () => {
  const classes = useStyles();
  const isAgentCartEnabled = useSelector(selectFeatureFlagAgentCart);
  const { suggestions, activeSuggestion, accept, dismiss } =
    useAISuggestionActions();

  // Alt+A / Alt+D mirror the card's Accept / Dismiss buttons. Registered before
  // the visibility short-circuit below so hook order stays stable; it no-ops
  // unless the co-pilot is enabled and a suggestion is showing. `event.code` is
  // matched (not `event.key`) so macOS Option-key glyphs don't break the match
  // (the omnibar's own onTextChange guard stops the glyph from being typed).
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyA' && e.code !== 'KeyD') {
      return;
    }
    if (!isAgentCartEnabled || !activeSuggestion) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'KeyA') {
      accept(activeSuggestion);
    } else {
      dismiss(activeSuggestion);
    }
  };
  // Capture phase so the shortcut fires even while the omnibar is focused.
  useEventListener('keydown', handleKeyDown, window, true);

  // The co-pilot advise loop is gated by the agent_cart flag (the same master
  // switch as the working-cart input side). With it off, never show suggestions
  // even if an ai_suggestion event slips through to the store.
  if (!isAgentCartEnabled || !suggestions.length) {
    return null;
  }

  return (
    <div className={classes.section} data-testid="ai-suggestions">
      <div className={classes.sectionHeader}>
        <AutoAwesomeOutlinedIcon className={classes.sectionIcon} />
        <span className={classes.sectionTitle}>AI Suggestions</span>
      </div>
      <div className={classes.container}>
        {suggestions.map((suggestion) => (
          <Paper
            key={suggestion.suggestion_id}
            className={classes.card}
            elevation={0}
          >
            <div className={classes.text}>
              <Typography
                className={classes.title}
                sx={{ fontSize: '0.75rem' }}
              >
                {suggestion.title}
              </Typography>
              {suggestion.reason && (
                <Typography
                  className={classes.reason}
                  sx={{ fontSize: '0.7rem' }}
                >
                  {suggestion.reason}
                </Typography>
              )}
            </div>
            <div className={classes.actions}>
              <IconButton
                size="small"
                color="success"
                title="Accept (Alt+A)"
                aria-label="Accept suggestion"
                onClick={() => accept(suggestion)}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="Dismiss (Alt+D)"
                aria-label="Dismiss suggestion"
                onClick={() => dismiss(suggestion)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </Paper>
        ))}
      </div>
    </div>
  );
};

export default AISuggestions;
